<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useHoliday, useHolidayAssets } from '~/composables/useHoliday'
import { usePageContext } from '@/composables/usePageContext'
import { useScreen } from '@/composables/useScreen'
import { useStaticAsset } from '@/composables/useStaticAsset'
import { getThumbnailUrl, getUrlFromList } from '@/helpers/videoUrlUtils'

import HelpButton from '@/ui-kit/layout/HelpButton.vue'
import ContactForm from '@/ui-kit/help/ContactForm.vue'
import PublicMobileNav from '@/ui-kit/public/MobileNav.vue'
import PublicVideoPlaylistCard from '@/ui-kit/public/VideoPlaylistCard.vue'
import PublicReviewCard from '@/ui-kit/public/ReviewCard.vue'
import PublicReviewVideo from '@/ui-kit/public/ReviewVideo.vue'
import PublicTeacherMap from '@/ui-kit/public/TeacherMap.vue'
import PublicPersonImage from '@/ui-kit/public/PersonImage.vue'
import PublicDemoVideoModal from '@/ui-kit/public/DemoVideoModal.vue'
import PublicSampleVideoModal from '@/ui-kit/public/SampleVideoModal.vue'
import PublicReviewModal from '@/ui-kit/public/ReviewModal.vue'
import VSlider from '@/ui-kit/components/Slider/Slider.vue'

type DemoVideoItem = {
  id: number
  name: string
  url: string
}

type DemoVideoCategory = {
  category_name: string
  videos: DemoVideoItem[]
}

type StaffMember = {
  name: string
  photo_url: string
  description: string
}

type StaffDepartment = {
  department_name: string
  members: StaffMember[]
}

type ReviewItem = {
  type: 't' | 'v'
  author_name: string
  photo: string
  date: string
  text: string
  url: string
}

type ReviewCategory = {
  category_name: string
  reviews: ReviewItem[]
}

type SampleVideo = {
  name: string
  url: string
  thumbnail_url: string | null
  teacher_name: string
  teacher_photo: string
  teacher_user_id: number
  teacher_bio_link: string
  date: string
  description: string
}

interface PageContext {
  before_and_after_videos: SampleVideo[]
  creative_project_videos: SampleVideo[]
  demo_videos: Record<string, DemoVideoCategory[]>
  staff: StaffDepartment[]
  reviews: ReviewCategory[]
  base_seminar_url: string
  student_demo_url: string
  banner?: {
    html: string
    updated_at: string
  }
  links: {
    oferta: string
    yandex_maps_api: string
    envybox_css: string
    envybox_js: string
  }
}

const ctx = usePageContext<PageContext>()
const { isMobile } = useScreen()
const asset = useStaticAsset()
const holiday = useHoliday()
const holidayAssets = useHolidayAssets()

const sliderConfig = {
  slidesPerView: 'auto',
  spaceBetween: 32,
  watchOverflow: true,
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev'
  }
}

const hideBanner = ref(false)
const desktopNavHidden = ref(false)
const schoolUnavailableModal = ref(false)

const selectedStaffDept = ref(0)
const selectedReviewCategory = ref(0)
const selectedPlaylist = ref<SampleVideo[] | null>(null)
const selectedDemoType = ref<string | null>(null)
const openedReview = ref<ReviewItem | null>(null)
const contactFormOpen = ref(false)

const reviewSlider = ref<InstanceType<typeof VSlider> | null>(null)
const desktopNav = ref<HTMLElement | null>(null)
const reviewBodyObserver = ref<ResizeObserver | null>(null)
const desktopNavObserver = ref<ResizeObserver | null>(null)

const studentAppsUrl = computed(() => {
  const url = new URL(ctx.student_demo_url, window.location.href)
  url.searchParams.set('tab', 'games')
  return url.toString()
})

const navLinks = [
  { text: 'О нас', href: 'https://promo.mirgovorit.ru/about' },
  { text: 'Методика', href: 'https://promo.mirgovorit.ru/method' },
  { text: 'Базовый семинар', href: 'https://promo.mirgovorit.ru/basic_seminar' },
  { text: 'Умный Учебник', href: 'https://promo.mirgovorit.ru/' },
  { text: 'Новости', href: 'https://promo.mirgovorit.ru/news' }
]

if (ctx.banner) {
  const hiddenBannerDate = localStorage.getItem('publicHideBanner')
  hideBanner.value = hiddenBannerDate === ctx.banner.updated_at

  if (hiddenBannerDate !== ctx.banner.updated_at) {
    localStorage.removeItem('publicHideBanner')
  }
}

watch(selectedReviewCategory, async () => {
  await nextTick()
  reviewSlider.value?.createSwiper()
  updateObserver()
})

const headerBackgroundImage = computed(() => {
  if (!holiday) return undefined
  return `url(${holidayAssets.headerPattern})`
})

onMounted(() => {
  reviewBodyObserver.value = new ResizeObserver(entries => {
    for (const entry of entries) {
      const clamped = entry.target.scrollHeight > entry.target.clientHeight
      const id = (entry.target as HTMLElement).dataset.card
      const button = document.querySelector(`a[data-card="${id}"]`) as HTMLElement | null

      if (!button) continue

      if (clamped) button.dataset.clamped = '1'
      else delete button.dataset.clamped
    }
  })

  if (desktopNav.value) {
    desktopNavObserver.value = new ResizeObserver(() => {
      if (!desktopNav.value) return
      desktopNavHidden.value = desktopNav.value.scrollWidth > desktopNav.value.clientWidth
    })
    desktopNavObserver.value.observe(desktopNav.value)
  }

  loadExternalStylesheet('main-page-envybox-css', ctx.links.envybox_css)
  void loadExternalScript('main-page-envybox-js', ctx.links.envybox_js, true)

  updateObserver()
})

onBeforeUnmount(() => {
  reviewBodyObserver.value?.disconnect()
  desktopNavObserver.value?.disconnect()
})

function loadExternalStylesheet(id: string, href: string) {
  if (document.getElementById(id)) return

  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

function loadExternalScript(id: string, src: string, isAsync = false) {
  const existing = document.getElementById(id) as HTMLScriptElement | null
  if (existing) {
    if (existing.dataset.loaded === '1') return Promise.resolve()

    return new Promise<void>(resolve => {
      existing.addEventListener('load', () => resolve(), { once: true })
    })
  }

  return new Promise<void>((resolve, reject) => {
    const script = document.createElement('script')
    script.id = id
    script.src = src
    script.async = isAsync
    script.onload = () => {
      script.dataset.loaded = '1'
      resolve()
    }
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.body.appendChild(script)
  })
}

function updateObserver() {
  const elements = document.querySelectorAll('.public-review-card__body')
  for (const element of elements) {
    reviewBodyObserver.value?.observe(element)
  }
}

function closeBanner() {
  if (!ctx.banner) return

  localStorage.setItem('publicHideBanner', ctx.banner.updated_at)
  hideBanner.value = true
}

function getRandomThumbnail(playlist: SampleVideo[]) {
  const item = playlist[Math.floor(Math.random() * playlist.length)]

  if (!item) return ''
  if (item.thumbnail_url) return item.thumbnail_url

  return getThumbnailUrl(getUrlFromList(item.url)) ?? ''
}
</script>

<template>
  <main v-cloak id="main-page" class="contents main-page">
    <HelpButton category-override="login" />

    <div v-if="ctx.banner && !hideBanner" class="main-page__banner" role="banner">
      <p class="main-page__banner-content" v-html="ctx.banner.html"></p>
      <v-link @click="closeBanner">
        <v-icon>mdi-close</v-icon>
      </v-link>
    </div>

    <img v-if="holidayAssets.foregroundPic" class="main-page__holiday-pic" :src="holidayAssets.foregroundPic" />

    <header class="main-page__header" :style="{ backgroundImage: headerBackgroundImage }">
      <PublicMobileNav v-if="desktopNavHidden || isMobile" class="mr-3" :items="navLinks" />

      <div class="main-page__logo">
        <v-logo :height="isMobile ? 40 : 64" />
      </div>

      <ul ref="desktopNav" class="main-page__desktop-nav" :hidden="desktopNavHidden || isMobile">
        <li v-for="(link, i) in navLinks" :key="i">
          <v-link :href="link.href" target="_blank">
            {{ link.text }}
          </v-link>
        </li>
      </ul>

      <v-button href="/users/login?student_login=true" :mode="isMobile ? 'primary text' : 'primary large'">
        <template v-if="isMobile">Войти</template>
        <template v-else>Войти в личный кабинет</template>
      </v-button>
    </header>

    <section class="main-page__hero mb-6">
      <div class="main-page__hero-main">
        <h1 class="main-page__title mb-3">Платформа МирГоворит</h1>
        <h2 class="main-page__subtitle mb-5">
          для взаимодействия школ, учителей, учеников и их родителей в системе Валерии Мещеряковой
          <b>Я ЛЮБЛЮ АНГЛИЙСКИЙ / I LOVE ENGLISH</b>
        </h2>

        <v-button mode="primary filled large" href="/public/study_in_joy" target="_blank">
          Подробнее о системе
        </v-button>
      </div>

      <div class="main-page__hero-demo">
        <h3 class="text-body mb-3">Выберите аккаунт и ознакомьтесь с функционалом личного кабинета</h3>

        <ul class="main-page__demo-cards">
          <li class="main-page__demo-card main-page__demo-card_teacher">
            <a href="/teacher/teacher_dashboard" class="main-page__demo-card-link" aria-label="Для учителя">
              <div aria-hidden class="main-page__demo-card-arrow">
                <v-icon size="24">mdi-arrow-top-right</v-icon>
              </div>

              <figure class="main-page__demo-card-content">
                <img :src="asset('img/main-page/demo-card-teacher.png')" />
                <figcaption>Для учителя</figcaption>
              </figure>
            </a>
          </li>

          <li class="main-page__demo-card main-page__demo-card_parents">
            <a href="/parent/cabinet" class="main-page__demo-card-link" aria-label="Для родителя">
              <div aria-hidden class="main-page__demo-card-arrow">
                <v-icon size="24">mdi-arrow-top-right</v-icon>
              </div>

              <figure class="main-page__demo-card-content">
                <img :src="asset('img/main-page/demo-card-parents.png')" />
                <figcaption>Для родителя</figcaption>
              </figure>
            </a>
          </li>

          <li class="main-page__demo-card main-page__demo-card_student">
            <a :href="ctx.student_demo_url" class="main-page__demo-card-link" aria-label="Для ученика">
              <div aria-hidden class="main-page__demo-card-arrow">
                <v-icon size="24">mdi-arrow-top-right</v-icon>
              </div>

              <figure class="main-page__demo-card-content">
                <img :src="asset('img/main-page/demo-card-student.png')" />
                <figcaption>Для ученика</figcaption>
              </figure>
            </a>
          </li>
        </ul>
      </div>
    </section>

    <section id="get-started" class="main-page__get-started mb-6">
      <h2 class="text-title-bold mb-3">С чего начать?</h2>
      <ul class="main-page__get-started-list">
        <li>
          <h3 class="text-body-bold">Учителю</h3>
          <p class="main-page__get-started-body mt-1 mb-2">
            Пройти базовый семинар по методу обучения детей 2–10 лет английскому языку по системе Валерии Мещеряковой
            Я ЛЮБЛЮ АНГЛИЙСКИЙ / I LOVE ENGLISH.
          </p>

          <v-button href="https://promo.mirgovorit.ru/basic_seminar" target="_blank" mode="warn filled">
            <v-icon class="mr-1">mdi-open-in-new</v-icon>
            Базовый семинар
          </v-button>
        </li>

        <li>
          <h3 class="text-body-bold">Родителю</h3>
          <p class="main-page__get-started-body mt-1 mb-2">
            Найти учителя, школу, детский центр (офлайн или онлайн), где ведутся занятия по методике Валерии
            Мещеряковой. А также использовать полезные игровые приложения для помощи своему ребёнку.
          </p>

          <div class="flex-adaptive">
            <v-button href="/online_teaching/find_teacher" mode="primary filled">
              <v-icon class="mr-1">mdi-open-in-new</v-icon>
              Найти учителя
            </v-button>

            <div class="mx-1 my-half" />

            <v-button :href="studentAppsUrl" target="_blank" mode="primary outlined"> Открыть приложения </v-button>
          </div>
        </li>
      </ul>
    </section>

    <section class="main-page__smartbook-ad align-center mb-6">
      <img :src="asset('img/smartbook-icon.svg')" height="120" class="mr-2" />

      <div class="flex-spacer my-1 my-d-0 mx-d-1">
        <h2 class="text-title-bold">Умный Учебник Я говорю по-английски / I Can Speak</h2>
        <ul class="mt-1">
          <li>— Электронное приложение для курса Я говорю по-английски / I Can Speak</li>
          <li class="mt-half">— 15 обучающих мультфильмов</li>
          <li class="mt-half">— Интерактивные задания, меняющиеся от просмотра к просмотру</li>
          <li class="mt-half">
            — Встроенные мотивационные кейсы для поддержания интереса к многоразовому прослушиванию материала
          </li>
        </ul>
      </div>

      <v-button mode="warn filled large" href="https://promo.mirgovorit.ru/"> Подробнее </v-button>
    </section>

    <section class="main-page__videos mb-6">
      <div class="main-page__videos-aside">
        <img width="80" :src="asset('img/main-page/ile.png')" />

        <h2 class="text-title-bold mt-3 mb-1">Методика Валерии Мещеряковой</h2>
        <h3 class="text-body">
          Посмотрите, какие результаты приобретают ученики в процессе обучения с нашими учителями в системе Я ЛЮБЛЮ
          АНГЛИЙСКИЙ / I LOVE ENGLISH, а также загляните в топ-видео с наших конкурсов.
        </h3>
      </div>

      <PublicVideoPlaylistCard
        :thumbnail-url="getRandomThumbnail(ctx.before_and_after_videos)"
        :video-count="ctx.before_and_after_videos.length"
        name="Видео До и После обучения"
        @click="selectedPlaylist = ctx.before_and_after_videos"
      />

      <PublicVideoPlaylistCard
        :thumbnail-url="getRandomThumbnail(ctx.creative_project_videos)"
        :video-count="ctx.creative_project_videos.length"
        name="Творческие проекты"
        @click="selectedPlaylist = ctx.creative_project_videos"
      />
    </section>

    <section id="map" class="main-page__map mb-6">
      <h2 class="text-title-bold mb-2">Наши учителя и школы на карте</h2>

      <ul class="main-page__map-legend mb-3">
        <li>
          <v-menu-base>
            <template #activator="{ onmouseenter, onmouseleave, onclick }">
              <span @mouseenter="onmouseenter" @mouseleave="onmouseleave" @click="onclick">
                <v-icon class="mr-1">map-marker-blue</v-icon>Учитель
              </span>
            </template>

            <p class="pa-1 main-page__map-legend-desc">Учитель, работающий по методике Мещеряковой.</p>
          </v-menu-base>
        </li>
        <li>
          <v-menu-base>
            <template #activator="{ onmouseenter, onmouseleave, onclick }">
              <span @mouseenter="onmouseenter" @mouseleave="onmouseleave" @click="onclick">
                <v-icon class="mr-1">map-marker-green</v-icon>Учитель АС
              </span>
            </template>

            <p class="pa-1 main-page__map-legend-desc">
              Учитель высокой квалификации, обладатель Авторского Сертификата (АС).
            </p>
          </v-menu-base>
        </li>
        <li>
          <v-menu-base>
            <template #activator="{ onmouseenter, onmouseleave, onclick }">
              <span @mouseenter="onmouseenter" @mouseleave="onmouseleave" @click="onclick">
                <v-icon class="mr-1">map-marker-orange</v-icon>Учитель АС + Тьютор
              </span>
            </template>

            <p class="pa-1 main-page__map-legend-desc">
              Учитель высокой квалификации, обладатель Авторского Сертификата (АС), прошедший Школу Тьюторов и имеющий
              право в рамках сайта давать консультации по системе Я ЛЮБЛЮ АНГЛИЙСКИЙ / I LOVE ENGLISH
            </p>
          </v-menu-base>
        </li>
        <li>
          <v-menu-base>
            <template #activator="{ onmouseenter, onmouseleave, onclick }">
              <span @mouseenter="onmouseenter" @mouseleave="onmouseleave" @click="onclick">
                <v-icon class="mr-1">map-marker-red</v-icon>Учитель АС + Методист
              </span>
            </template>

            <p class="pa-1 main-page__map-legend-desc">
              Учитель высокой квалификации, обладатель Авторского Сертификата (АС), обучающий учителей методике Я ЛЮБЛЮ
              АНГЛИЙСКИЙ / I LOVE ENGLISH на сайте МирГоворит.
            </p>
          </v-menu-base>
        </li>
      </ul>

      <PublicTeacherMap />

      <v-link class="mt-3" mode="primary" href="/teacher/edit_profile#add-to-map">
        <v-icon class="mr-1">mdi-map-marker-plus</v-icon>
        Добавить себя на карту
      </v-link>
    </section>

    <section class="main-page__people mb-6">
      <h2 class="text-title-bold mb-2">Наша команда</h2>

      <div class="main-page__link-group mb-3">
        <a
          v-for="(dept, i) in ctx.staff"
          :key="i"
          :aria-disabled="selectedStaffDept === i || undefined"
          @click="selectedStaffDept = i"
          v-text="dept.department_name"
        ></a>
      </div>

      <ul v-if="selectedStaffDept in ctx.staff" class="main-page__people-list">
        <li v-for="(member, i) in ctx.staff[selectedStaffDept].members" :key="i" class="main-page__people-list-item">
          <PublicPersonImage :index="i" :src="member.photo_url" />

          <span v-text="member.name"></span>

          <p class="main-page__people-item-description" v-text="member.description"></p>
        </li>
      </ul>
    </section>

    <section id="reviews" class="main-page__reviews mb-6">
      <h2 class="text-title-bold mb-2">Отзывы</h2>

      <div class="main-page__link-group mb-3">
        <a
          v-for="(category, i) in ctx.reviews"
          :key="i"
          :aria-disabled="selectedReviewCategory === i || undefined"
          @click="selectedReviewCategory = i"
          v-text="category.category_name"
        ></a>
      </div>

      <PublicReviewModal v-model:review="openedReview" />

      <VSlider
        v-if="selectedReviewCategory in ctx.reviews"
        ref="reviewSlider"
        class="main-page__review-list"
        :config="sliderConfig"
      >
        <template v-for="(review, i) in ctx.reviews[selectedReviewCategory].reviews" :key="i">
          <PublicReviewCard
            v-if="review.type === 't'"
            class="swiper-slide"
            :avatar-url="review.photo"
            :author-name="review.author_name"
            :date="review.date"
            :href="review.url"
            @open-review="openedReview = review"
          >
            {{ review.text }}
          </PublicReviewCard>

          <PublicReviewVideo
            v-else-if="review.type === 'v'"
            class="swiper-slide"
            :thumbnail-url="review.photo"
            :author-name="review.author_name"
            :date="review.date"
            @open-review="openedReview = review"
          >
            {{ review.text }}
          </PublicReviewVideo>
        </template>
      </VSlider>
    </section>

    <section class="main-page__contact-form mb-6">
      <v-bitrix-form-modal></v-bitrix-form-modal>
    </section>

    <footer class="main-page__footer">
      <div class="main-page__footer-content">
        <div class="d-flex flex-column align-start">
          <img height="48" :src="asset('img/logo.svg')" />

          <template v-if="!isMobile">
            <span class="flex-spacer my-1"></span>

            <p class="text-body-bold">ИП Мещеряков О.Ю.</p>

            <p class="text-body mt-2">ОГРНИП 321169000014352</p>
            <p class="text-body mt-1">ИНН 165703420351</p>
          </template>
        </div>

        <ul class="main-page__footer-list">
          <li class="main-page__footer-list-title">Навигация</li>

          <li>
            <v-link href="/public/study_in_joy" target="_blank"> Подробнее о системе </v-link>
          </li>
          <li class="mt-2">
            <v-link href="#get-started">С чего начать</v-link>
          </li>
          <li class="mt-2">
            <v-link href="#map">Карта учителей и школ</v-link>
          </li>
          <li class="mt-2">
            <v-link href="#reviews">Отзывы о нас</v-link>
          </li>
        </ul>

        <ul class="main-page__footer-list">
          <li class="main-page__footer-list-title">Общая информация</li>

          <li>
            <v-link :href="ctx.links.oferta" target="_blank"> Пользовательское соглашение </v-link>
          </li>
          <li class="mt-2">
            <v-link href="/help/center" target="_blank"> Справочный центр </v-link>
          </li>
        </ul>

        <ul class="main-page__footer-list">
          <li class="main-page__footer-list-title">Контакты</li>

          <li>
            <v-link href="https://t.me/Mirgovorit_bot" target="_blank"> Техподдержка </v-link>
          </li>
          <li class="mt-2">
            <v-link @click="contactFormOpen = true"> Обратная связь </v-link>
          </li>

          <div class="d-flex mt-4">
            <v-link href="https://vk.com/ilemirgovorit" target="_blank" class="mr-2">
              <v-icon size="40">social-vk</v-icon>
            </v-link>
            <v-link href="https://t.me/ileconf2024" target="_blank" class="mr-2">
              <v-icon size="40">social-tg</v-icon>
            </v-link>
            <v-link href="https://www.youtube.com/@mirgovorit" target="_blank">
              <v-icon size="40">social-yt</v-icon>
            </v-link>
          </div>
        </ul>
      </div>

      <div v-if="isMobile" class="d-flex flex-column mt-5">
        <p class="text-body-bold">ИП Мещеряков О.Ю.</p>

        <p class="text-body mt-2">ОГРНИП 321169000014352</p>
        <p class="text-body mt-1">ИНН 165703420351</p>
      </div>

      <div class="flex-adaptive justify-end mt-5 gray-text">
        <span> &copy; Мещерякова В.Н. 2020 </span>
        <span class="mx-3 mt-1"></span>
        <span> &copy; Кирякин И.В. 2020 </span>
      </div>
    </footer>

    <PublicDemoVideoModal
      :model-value="selectedDemoType ?? undefined"
      :playlist="ctx.demo_videos[selectedDemoType || ''] || []"
      @update:model-value="
        () => {
          selectedDemoType = null
        }
      "
    />

    <PublicSampleVideoModal
      :model-value="selectedPlaylist ?? undefined"
      @update:model-value="
        () => {
          selectedPlaylist = null
        }
      "
    />

    <v-rich-modal v-model="schoolUnavailableModal" title="МирГоворит для школы">
      Скоро будет доступно! Раздел в разработке.

      <template #actions>
        <v-button mode="primary filled block" @click="schoolUnavailableModal = false"> Хорошо </v-button>
      </template>
    </v-rich-modal>

    <ContactForm v-model="contactFormOpen" />
  </main>
</template>

<style lang="scss">
@use '@/styles/pages/public/main_page.scss' as *;
@import url('https://fonts.googleapis.com/css2?family=Caveat:wght@700&display=swap');
</style>
